import { EditableModelName } from '@/shared/types/editable';
import { ClientType } from '@/shared/types/enum';
import { ModelName } from '@/shared/types/model';
import { PlanInterval } from '@/shared/types/plan';

const route = (...parts: (string | false | undefined)[]) =>
  '/' +
  parts
    .filter((part) => part)
    .map((part) => part as string)
    .map((part) => (part.startsWith('/') ? part.substring(1) : part))
    .join('/');

const resourceRoute = (resource: string, resourceOptions?: { prefix?: string; archivable?: boolean }) => {
  return {
    list: (params?: { archived?: boolean }) =>
      route(
        resourceOptions?.prefix,
        resource,
        resourceOptions?.archivable && (!params ? ':archived(archived)?' : params.archived && 'archived'),
      ),
    create: () => route(resourceOptions?.prefix, resource, 'new'),
    edit: (params?: { id?: string }) => route(resourceOptions?.prefix, resource, !params ? ':id' : params.id),
    archive: (params?: { id?: string }) => route(resource, !params ? ':id' : params.id, 'archive'),
    unarchive: (params?: { id?: string }) => route(resource, !params ? ':id' : params.id, 'unarchive'),
  };
};

export const routes = {
  root: () => route(''),

  signUp: () => route('sign-up'),
  signIn: () => route('sign-in'),
  signInVerify: () => route('sign-in/verify'),
  signInMfaSetup: () => route('sign-in/mfa-setup'),
  signOut: () => route('sign-out'),

  onboarding: () => route('onboarding'),

  subscribe: () => route('subscribe'),

  password: {
    reset: () => route('password', 'reset'),
    confirm: (params?: { token?: string; email?: string }) =>
      route('password', 'confirm', !params ? ':token' : params.token, !params ? ':email' : params.email),
  },

  email: {
    verify: () => route('email', 'verify'),
    verifyToken: (params?: { token?: string; email?: string }) =>
      route('email', 'verify', !params ? ':token' : params.token, !params ? ':email' : params.email),
  },

  account: {
    settings: () => route('account', 'settings'),
    integrations: () => route('account', 'integrations'),
    billing: () => route('account', 'billing'),
    subscribe: (params?: { code: string; interval: PlanInterval }) =>
      route('account', 'subscribe', !params ? ':code' : params.code, !params ? ':interval' : params.interval),
    onboarding: {
      updateStep: () => route('account', 'onboarding', 'update-step'),
    },
    export: () => route('account', 'export'),
    signingAuthorisation: () => route('account', 'signing-authorisation'),
    xama: () => route('account', 'integrations', 'xama'),
    freeagent: () => route('account', 'integrations', 'freeagent'),
    freeagentCallback: () => route('account', 'integrations', 'freeagent', 'callback'),
    apiKeys: {
      create: () => route('account', 'integrations', 'api-keys'),
      revoke: (params?: { keyId?: string }) =>
        route('account', 'integrations', 'api-keys', !params?.keyId ? ':keyId' : params.keyId),
    },
    portalLogo: () => route('account', 'portal-logo'),
    emailDomain: () => route('account', 'email-domain'),
    emailDomainVerify: () => route('account', 'email-domain', 'verify'),
    purchase: {
      envelopes: (params?: { quantity: string }) =>
        route('account', 'purchase', 'envelopes', !params ? ':quantity' : params.quantity),
    },
    reset: () => route('account', 'reset'),
  },

  members: {
    ...resourceRoute('users', { archivable: true }),
    resendInvite: (params?: { id?: string }) => route('users', !params ? ':id' : params.id, 'resend-invite'),
    resetPassword: (params?: { id?: string }) => route('users', !params ? ':id' : params.id, 'reset-password'),
    enableMfa: (params?: { id?: string }) => route('users', !params ? ':id' : params.id, 'enable-mfa'),
    confirmMfa: (params?: { id?: string }) => route('users', !params ? ':id' : params.id, 'confirm-mfa'),
    disableMfa: (params?: { id?: string }) => route('users', !params ? ':id' : params.id, 'disable-mfa'),
    setTaskSummaryEmail: (params?: { id?: string }) =>
      route('users', !params ? ':id' : params.id, 'task-summary-email'),
  },

  dashboard: () => route('dashboard'),

  organisations: {
    ...resourceRoute('organisations', { archivable: true }),
    import: {
      upload: () => route('organisations', 'import'),
      delete: (params?: { id?: string }) => route('organisations', 'import', !params ? ':id' : params.id),
    },
    failedImportCount: () => route('organisations', 'failed-import-count'),
    companiesHouseSearch: () => route('organisations', 'companies-house', 'search'),
    sync: (params?: { id?: string }) => route('organisations', !params ? ':id' : params.id, 'sync'),
    syncPeople: (params?: { id?: string }) => route('organisations', !params ? ':id' : params.id, 'sync', 'people'),
    companiesHousePersonMatches: (params?: { id?: string }) =>
      route('organisations', !params ? ':id' : params.id, 'companies-house-people', 'matches'),
    legacyPrimaryContact: (params?: { id?: string }) =>
      route('organisations', !params ? ':id' : params.id, 'legacy-primary-contact'),
    legacyPrimaryContactMatches: (params?: { id?: string }) =>
      route('organisations', !params ? ':id' : params.id, 'legacy-primary-contact', 'matches'),
    legacyPrimaryContactMigrate: (params?: { id?: string }) =>
      route('organisations', !params ? ':id' : params.id, 'legacy-primary-contact', 'migrate'),
    amlCheck: (params?: { id?: string }) => route('organisations', !params ? ':id' : params.id, 'aml', 'check'),
    amlSync: (params?: { id?: string }) => route('organisations', !params ? ':id' : params.id, 'aml', 'sync'),
    amlUpdate: (params?: { id?: string }) => route('organisations', !params ? ':id' : params.id, 'aml', 'update'),
    xamaSearch: (params?: { id?: string }) => route('organisations', !params ? ':id' : params.id, 'xama', 'search'),
    attachmentsTempUrl: (params?: { id?: string }) =>
      route('organisations', !params ? ':id' : params.id, 'attachments', 'temp-url'),
    attachmentsCreate: (params?: { id?: string }) =>
      route('organisations', !params ? ':id' : params.id, 'attachments', 'new'),
    attachmentsAdd: (params?: { id?: string }) => route('organisations', !params ? ':id' : params.id, 'attachments'),
    attachmentsDelete: (params?: { id?: string; key?: string }) =>
      route('organisations', !params ? ':id' : params.id, 'attachments', !params ? ':key' : params.key),
    attachmentsGet: (params?: { id?: string; key?: string }) =>
      route('organisations', !params ? ':id' : params.id, 'attachments', !params ? ':key' : params.key),
    xamaAccount: (params?: { id?: string; xamaAccountId?: string }) =>
      route(
        'organisations',
        !params ? ':id' : params.id,
        'xama',
        'account',
        !params ? ':xamaAccountId' : params.xamaAccountId,
      ),
    xamaLink: (params?: { id?: string }) => route('organisations', !params ? ':id' : params.id, 'xama', 'link'),
    xamaUnlink: (params?: { id?: string }) => route('organisations', !params ? ':id' : params.id, 'xama', 'unlink'),
  },

  individuals: {
    ...resourceRoute('individuals', { archivable: true }),
    import: {
      upload: () => route('individuals', 'import'),
      delete: (params?: { id?: string }) => route('individuals', 'import', !params ? ':id' : params.id),
    },
    amlCheck: (params?: { id?: string }) => route('individuals', !params ? ':id' : params.id, 'aml', 'check'),
    amlSync: (params?: { id?: string }) => route('individuals', !params ? ':id' : params.id, 'aml', 'sync'),
    amlUpdate: (params?: { id?: string }) => route('individuals', !params ? ':id' : params.id, 'aml', 'update'),
    xamaSearch: (params?: { id?: string }) => route('individuals', !params ? ':id' : params.id, 'xama', 'search'),
    attachmentsTempUrl: (params?: { id?: string }) =>
      route('individuals', !params ? ':id' : params.id, 'attachments', 'temp-url'),
    attachmentsCreate: (params?: { id?: string }) =>
      route('individuals', !params ? ':id' : params.id, 'attachments', 'new'),
    attachmentsAdd: (params?: { id?: string }) => route('individuals', !params ? ':id' : params.id, 'attachments'),
    attachmentsDelete: (params?: { id?: string; key?: string }) =>
      route('individuals', !params ? ':id' : params.id, 'attachments', !params ? ':key' : params.key),
    attachmentsGet: (params?: { id?: string; key?: string }) =>
      route('individuals', !params ? ':id' : params.id, 'attachments', !params ? ':key' : params.key),
    xamaAccount: (params?: { id?: string; xamaAccountId?: string }) =>
      route(
        'individuals',
        !params ? ':id' : params.id,
        'xama',
        'account',
        !params ? ':xamaAccountId' : params.xamaAccountId,
      ),
    xamaLink: (params?: { id?: string }) => route('individuals', !params ? ':id' : params.id, 'xama', 'link'),
    xamaUnlink: (params?: { id?: string }) => route('individuals', !params ? ':id' : params.id, 'xama', 'unlink'),
  },

  serviceManagers: resourceRoute('service-managers'),
  associations: {
    ...resourceRoute('associations'),
    primary: (params?: { id?: string }) => route('associations', !params ? ':id' : params.id, 'primary'),
  },

  services: resourceRoute('services', { archivable: true }),

  tasks: {
    ...resourceRoute('tasks'),
    copy: (params?: { id?: string }) => route('tasks', !params ? ':id' : params.id, 'copy'),
    subTasks: (params?: { id?: string }) => route('tasks', !params ? ':id' : params.id, 'sub-tasks'),
    amlContext: (params?: { id?: string }) => route('tasks', !params ? ':id' : params.id, 'aml-context'),
    attachmentsTempUrl: (params?: { id?: string }) =>
      route('tasks', !params ? ':id' : params.id, 'attachments', 'temp-url'),
    attachmentsCreate: (params?: { id?: string }) => route('tasks', !params ? ':id' : params.id, 'attachments', 'new'),
    attachmentsAdd: (params?: { id?: string }) => route('tasks', !params ? ':id' : params.id, 'attachments'),
    attachmentsDelete: (params?: { id?: string; key?: string }) =>
      route('tasks', !params ? ':id' : params.id, 'attachments', !params ? ':key' : params.key),
    attachmentsGet: (params?: { id?: string; key?: string }) =>
      route('tasks', !params ? ':id' : params.id, 'attachments', !params ? ':key' : params.key),
  },

  documents: {
    list: () => route('documents'),
    edit: (params?: { id?: string }) => route('documents', !params ? ':id' : params.id),
    download: (params?: { location?: string; parentId?: string; key?: string }) =>
      route(
        'documents',
        'download',
        !params ? ':location' : params.location,
        !params ? ':parentId' : params.parentId,
        !params ? ':key' : params.key,
      ),
    bulkDownload: () => route('documents', 'bulk-download'),
  },

  smartTasks: {
    ...resourceRoute('smart-tasks'),
    preview: () => route('smart-tasks-templates', 'preview'),
    offsets: (params?: { id?: string }) => route('smart-tasks-templates', !params ? ':id' : params.id, 'offsets'),
    subTasks: (params?: { id?: string }) => route('smart-tasks-templates', !params ? ':id' : params.id, 'sub-tasks'),
    options: {
      types: (params?: { clientType?: ClientType }) =>
        route(
          'smart-tasks-templates',
          'options',
          'types',
          !params ? `:clientType(${Object.values(ClientType).join('|')})` : params.clientType,
        ),
      updateFields: (params?: { clientType?: ClientType }) =>
        route(
          'smart-tasks-templates',
          'options',
          'update-fields',
          !params ? `:clientType(${Object.values(ClientType).join('|')})` : params.clientType,
        ),
    },
  },

  recurringTasks: {
    ...resourceRoute('recurring-tasks'),
    preview: () => route('recurring-tasks', 'preview'),
    copy: (params?: { id?: string }) => route('recurring-tasks', !params ? ':id' : params.id, 'copy'),
    offsets: (params?: { id?: string }) => route('recurring-tasks', !params ? ':id' : params.id, 'offsets'),
    subTasks: (params?: { id?: string }) => route('recurring-tasks', !params ? ':id' : params.id, 'sub-tasks'),
  },

  messages: resourceRoute('messages'),

  requests: {
    ...resourceRoute('requests'),
    downloadTempUrl: () => route('requests', 'download-temp-url'),
    copyUpload: (params?: { id?: string; fileKey?: string }) =>
      route('requests', !params ? ':id' : params.id, 'uploads', !params ? ':fileKey' : params.fileKey, 'copy'),
    resendSigning: (params?: { id?: string }) => route('requests', !params ? ':id' : params.id, 'resend-signing'),
    editSigning: (params?: { id?: string }) => route('requests', !params ? ':id' : params.id, 'edit-signing'),
  },

  customFields: () => route('custom-fields'),

  emailTemplates: {
    ...resourceRoute('email-templates'),
    preview: () => route('email-templates', 'preview'),
    search: () => route('email-templates', 'search'),
  },

  forms: resourceRoute('forms'),

  checklistTemplates: {
    ...resourceRoute('checklist-templates'),
    options: () => route('checklist-templates', 'options'),
  },

  admin: {
    accounts: {
      ...resourceRoute('accounts', { prefix: 'admin' }),
      manage: () => route('admin', 'accounts', 'manage'),
      createSmartTasks: (params?: { id?: string }) =>
        route('admin', 'accounts', !params ? ':id' : params.id, 'create-smart-tasks'),
      requestGdprErasure: (params?: { id?: string }) =>
        route('admin', 'accounts', !params ? ':id' : params.id, 'request-gdpr-erasure'),
    },
  },

  data: {
    grid: (params?: { modelName?: ModelName; archived?: boolean }) =>
      route(
        'data',
        'grid',
        !params ? ':model' : params.modelName,
        !params ? ':archived?' : params.archived && 'archived',
      ),

    editable: (params?: { model?: EditableModelName }) => route('data', 'editable', !params ? ':model' : params.model),

    bulkUpdate: (params?: { model?: ModelName }) => route('data', 'bulk-update', !params ? ':model' : params.model),

    lookup: {
      list: (params?: { model?: ModelName }) => route('data', 'lookup', !params ? ':model' : params.model),
      item: (params?: { model?: ModelName; value?: string }) =>
        route('data', 'lookup', !params ? ':model' : params.model, !params ? ':value' : params.value),
    },

    customFields: () => route('data', 'custom-fields'),

    search: () => route('data', 'search'),

    uploads: {
      get: (template = true) => route('data', 'uploads', template ? ':name' : ''),
      put: () => route('data', 'uploads', 'put'),
      delete: () => route('data', 'uploads', 'delete'),
    },
  },

  settings: {
    grid: (params?: { id?: string; view?: string }) =>
      route('settings', 'grid', !params ? ':id' : params.id, !params ? ':view?' : params.view),
  },

  portal: {
    signIn: (params?: { portalSlug?: string }) => route(!params ? ':portalSlug' : params.portalSlug, 'sign-in'),
    signInSent: (params?: { portalSlug?: string }) =>
      route(!params ? ':portalSlug' : params.portalSlug, 'sign-in', 'sent'),
    signInVerify: (params?: { portalSlug?: string; token?: string; email?: string }) =>
      route(
        !params ? ':portalSlug' : params.portalSlug,
        'sign-in',
        'verify',
        !params ? ':token' : params.token,
        !params ? ':email' : params.email,
      ),
    signInVerifyCode: (params?: { portalSlug?: string }) =>
      route(!params ? ':portalSlug' : params.portalSlug, 'sign-in', 'verify'),
    signOut: (params?: { portalSlug?: string }) => route(!params ? ':portalSlug' : params.portalSlug, 'sign-out'),
    task: (params?: { portalSlug?: string; taskId?: string }) =>
      route(!params ? ':portalSlug' : params.portalSlug, 't', !params ? ':taskId' : params.taskId),
    request: (params?: { portalSlug?: string; requestId?: string }) =>
      route(!params ? ':portalSlug' : params.portalSlug, 'r', !params ? ':requestId' : params.requestId),
    download: (params?: { portalSlug?: string; requestId?: string }) =>
      route(!params ? ':portalSlug' : params.portalSlug, 'd', !params ? ':requestId' : params.requestId),
    downloadFile: (params?: { portalSlug?: string; requestId?: string; fileKey?: string }) =>
      route(
        !params ? ':portalSlug' : params.portalSlug,
        'd',
        !params ? ':requestId' : params.requestId,
        !params ? ':fileKey' : params.fileKey,
      ),
  },

  unauthorised: route('unauthorised'),
  notFound: route('not-found'),
  error: route('error'),

  test: {
    frontendError: () => route('test', 'frontend-error'),
    backendError: () => route('test', 'backend-error'),
  },
};
